import { BUSINESS_ENTITY_TYPES, type EquipmentItemView, FINANCIAL_FACT_SOURCES, FINANCIAL_PERIODS, type MoneyView } from '@tex/finance-shared'
import { Plus, Trash2 } from 'lucide-react'
import type { ReactNode } from 'react'
import {
  type AddressDraft,
  type BusinessDraft,
  emptyOwner,
  type EquipmentReviewDraft,
  FINANCIAL_PERIOD_LABELS,
  FINANCIAL_SOURCE_LABELS,
  type FinancialFactDraft,
  type FinancialsDraft,
  type OwnerDraft,
} from '../lib/application-form'
import { ENTITY_TYPE_LABELS } from '../lib/case-form'
import { formatAmount, humanize } from '../lib/format'
import { MoneyInput, StateSelect } from './case-form'
import { Button, Field, Input, Mono, Select, Textarea } from './ui'

type Setter<T> = (update: (prev: T) => T) => void

function Grid({ children, cols = 2 }: { children: ReactNode; cols?: 2 | 3 }) {
  return <div className={`grid grid-cols-1 gap-3 ${cols === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2'}`}>{children}</div>
}

function AddressFields({ draft, set, disabled }: { draft: AddressDraft; set: Setter<AddressDraft>; disabled?: boolean }) {
  const f = <K extends keyof AddressDraft>(k: K) => (v: string) => set((a) => ({ ...a, [k]: v }))
  return (
    <div className="space-y-3">
      <Field label="Street">
        <Input value={draft.line1} onChange={(e) => f('line1')(e.target.value)} autoComplete="address-line1" disabled={disabled} />
      </Field>
      <Field label="Suite / line 2">
        <Input value={draft.line2} onChange={(e) => f('line2')(e.target.value)} autoComplete="address-line2" disabled={disabled} />
      </Field>
      <Grid cols={3}>
        <Field label="City">
          <Input value={draft.city} onChange={(e) => f('city')(e.target.value)} autoComplete="address-level2" disabled={disabled} />
        </Field>
        <Field label="State">
          <StateSelect value={draft.state} onChange={f('state')} disabled={disabled} />
        </Field>
        <Field label="ZIP">
          <Input value={draft.postal_code} onChange={(e) => f('postal_code')(e.target.value)} inputMode="numeric" autoComplete="postal-code" disabled={disabled} />
        </Field>
      </Grid>
    </div>
  )
}

function Toggle({ checked, onChange, disabled, children }: { checked: boolean; onChange: (v: boolean) => void; disabled?: boolean; children: ReactNode }) {
  return (
    <label className="flex items-start gap-2 text-sm">
      <input type="checkbox" className="mt-0.5" checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} />
      <span>{children}</span>
    </label>
  )
}

// ── business ──

export function BusinessStepForm({ draft, set, disabled }: { draft: BusinessDraft; set: Setter<BusinessDraft>; disabled?: boolean }) {
  const field = <K extends keyof BusinessDraft>(k: K) => (v: BusinessDraft[K]) => set((d) => ({ ...d, [k]: v }))
  const rep = <K extends keyof BusinessDraft['representative']>(k: K) => (v: string) => set((d) => ({ ...d, representative: { ...d.representative, [k]: v } }))
  return (
    <div className="space-y-5">
      <Grid>
        <Field label="Legal business name" hint="As it appears on your formation documents.">
          <Input value={draft.legal_name} onChange={(e) => field('legal_name')(e.target.value)} autoComplete="organization" disabled={disabled} />
        </Field>
        <Field label="Trade name / DBA (optional)">
          <Input value={draft.trading_name} onChange={(e) => field('trading_name')(e.target.value)} disabled={disabled} />
        </Field>
        <Field label="Entity type">
          <Select value={draft.entity_type} onChange={(e) => field('entity_type')(e.target.value as BusinessDraft['entity_type'])} disabled={disabled}>
            <option value="">Choose…</option>
            {BUSINESS_ENTITY_TYPES.map((t) => (
              <option key={t} value={t}>
                {ENTITY_TYPE_LABELS[t]}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Industry" hint="A few words, e.g. site preparation, hauling, ranching.">
          <Input value={draft.industry} onChange={(e) => field('industry')(e.target.value)} disabled={disabled} />
        </Field>
        <Field label="Date the business was formed">
          <Input type="date" value={draft.formed_on} onChange={(e) => field('formed_on')(e.target.value)} disabled={disabled} />
        </Field>
        <Field label="Operating since (if different)">
          <Input type="date" value={draft.operating_since} onChange={(e) => field('operating_since')(e.target.value)} disabled={disabled} />
        </Field>
      </Grid>

      <fieldset className="rounded-md border border-[var(--border-subtle)] p-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Business address</legend>
        <AddressFields draft={draft.business_address} set={(u) => set((d) => ({ ...d, business_address: u(d.business_address) }))} disabled={disabled} />
      </fieldset>

      <fieldset className="rounded-md border border-[var(--border-subtle)] p-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Where the equipment will be kept</legend>
        <Toggle checked={draft.same_use_address} onChange={field('same_use_address')} disabled={disabled}>
          Same as the business address
        </Toggle>
        {!draft.same_use_address && (
          <div className="mt-3">
            <AddressFields draft={draft.equipment_use_address} set={(u) => set((d) => ({ ...d, equipment_use_address: u(d.equipment_use_address) }))} disabled={disabled} />
          </div>
        )}
      </fieldset>

      <fieldset className="rounded-md border border-[var(--border-subtle)] p-3">
        <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">You, the person applying</legend>
        <Grid>
          <Field label="Your name">
            <Input value={draft.representative.name} onChange={(e) => rep('name')(e.target.value)} autoComplete="name" disabled={disabled} />
          </Field>
          <Field label="Your title" hint="Owner, managing member, CFO…">
            <Input value={draft.representative.title} onChange={(e) => rep('title')(e.target.value)} autoComplete="organization-title" disabled={disabled} />
          </Field>
          <Field label="Email">
            <Input type="email" value={draft.representative.email} onChange={(e) => rep('email')(e.target.value)} autoComplete="email" disabled={disabled} />
          </Field>
          <Field label="Phone">
            <Input type="tel" value={draft.representative.phone} onChange={(e) => rep('phone')(e.target.value)} autoComplete="tel" disabled={disabled} />
          </Field>
        </Grid>
      </fieldset>
    </div>
  )
}

// ── owners ──

export function OwnersStepForm({ owners, set, disabled }: { owners: OwnerDraft[]; set: Setter<OwnerDraft[]>; disabled?: boolean }) {
  const update = (key: string, patch: (o: OwnerDraft) => OwnerDraft) => set((list) => list.map((o) => (o.key === key ? patch(o) : o)))
  const total = owners.reduce((acc, o) => acc + (Number(o.ownership_percent) || 0), 0)
  return (
    <div className="space-y-4">
      {owners.map((o, i) => (
        <fieldset key={o.key} className="rounded-md border border-[var(--border-subtle)] p-3">
          <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">Owner {i + 1}</legend>
          <div className="space-y-3">
            <Grid>
              <Field label="Full name">
                <Input value={o.full_name} onChange={(e) => update(o.key, (x) => ({ ...x, full_name: e.target.value }))} disabled={disabled} />
              </Field>
              <Field label="Title">
                <Input value={o.title} onChange={(e) => update(o.key, (x) => ({ ...x, title: e.target.value }))} disabled={disabled} />
              </Field>
              <Field label="Ownership %">
                <Input
                  inputMode="numeric"
                  value={o.ownership_percent}
                  onChange={(e) => update(o.key, (x) => ({ ...x, ownership_percent: e.target.value }))}
                  className="font-mono tabular-nums"
                  disabled={disabled}
                />
              </Field>
              <Field label="Email">
                <Input type="email" value={o.email} onChange={(e) => update(o.key, (x) => ({ ...x, email: e.target.value }))} disabled={disabled} />
              </Field>
              <Field label="Phone (optional)">
                <Input type="tel" value={o.phone} onChange={(e) => update(o.key, (x) => ({ ...x, phone: e.target.value }))} disabled={disabled} />
              </Field>
            </Grid>
            <Toggle checked={o.has_address} onChange={(v) => update(o.key, (x) => ({ ...x, has_address: v }))} disabled={disabled}>
              Add this owner&rsquo;s address
            </Toggle>
            {o.has_address && <AddressFields draft={o.address} set={(u) => update(o.key, (x) => ({ ...x, address: u(x.address) }))} disabled={disabled} />}
            {owners.length > 1 && (
              <div className="flex justify-end">
                <Button type="button" variant="danger" onClick={() => set((list) => list.filter((x) => x.key !== o.key))} disabled={disabled}>
                  <Trash2 className="mr-1 h-4 w-4" /> Remove owner
                </Button>
              </div>
            )}
          </div>
        </fieldset>
      ))}
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <span className={total > 100 ? 'text-tex-red' : 'text-neutral-500'}>
          Listed ownership: <Mono>{total}%</Mono>
          {total > 100 && ' — more than 100%'}
        </span>
        <Button type="button" variant="secondary" onClick={() => set((list) => [...list, emptyOwner()])} disabled={disabled || owners.length >= 10}>
          <Plus className="mr-1 h-4 w-4" /> Add an owner
        </Button>
      </div>
    </div>
  )
}

// ── financials ──

function FactFields({ label, hint, draft, set, disabled }: { label: string; hint: string; draft: FinancialFactDraft; set: Setter<FinancialFactDraft>; disabled?: boolean }) {
  const f = <K extends keyof FinancialFactDraft>(k: K) => (v: FinancialFactDraft[K]) => set((d) => ({ ...d, [k]: v }))
  return (
    <fieldset className="rounded-md border border-[var(--border-subtle)] p-3">
      <legend className="px-1 text-xs font-medium uppercase tracking-wide text-neutral-500">{label}</legend>
      <p className="mb-3 text-xs text-neutral-500">{hint}</p>
      <Toggle checked={!draft.known} onChange={(v) => f('known')(!v)} disabled={disabled}>
        I don&rsquo;t know this yet
      </Toggle>
      {draft.known && (
        <div className="mt-3 space-y-3">
          <Grid>
            <Field label="Amount">
              <MoneyInput value={draft.amount} onChange={f('amount')} disabled={disabled} />
            </Field>
            <Field label="Period">
              <Select value={draft.period} onChange={(e) => f('period')(e.target.value as FinancialFactDraft['period'])} disabled={disabled}>
                {FINANCIAL_PERIODS.map((p) => (
                  <option key={p} value={p}>
                    {FINANCIAL_PERIOD_LABELS[p]}
                  </option>
                ))}
              </Select>
            </Field>
            <Field label="Period ends (optional)">
              <Input type="date" value={draft.period_end} onChange={(e) => f('period_end')(e.target.value)} disabled={disabled} />
            </Field>
            <Field label="Based on">
              <Select value={draft.source} onChange={(e) => f('source')(e.target.value as FinancialFactDraft['source'])} disabled={disabled}>
                {FINANCIAL_FACT_SOURCES.map((s) => (
                  <option key={s} value={s}>
                    {FINANCIAL_SOURCE_LABELS[s]}
                  </option>
                ))}
              </Select>
            </Field>
          </Grid>
        </div>
      )}
    </fieldset>
  )
}

export function FinancialsStepForm({
  draft,
  set,
  dealerRequested,
  disabled,
}: {
  draft: FinancialsDraft
  set: Setter<FinancialsDraft>
  dealerRequested: MoneyView
  disabled?: boolean
}) {
  const field = <K extends keyof FinancialsDraft>(k: K) => (v: FinancialsDraft[K]) => set((d) => ({ ...d, [k]: v }))
  return (
    <div className="space-y-4">
      <Field label="Amount you are asking to finance" hint={`The dealer's request is for ${formatAmount(dealerRequested)}. Enter what you actually want; a difference is flagged for Tex, not hidden.`}>
        <MoneyInput value={draft.requested_amount} onChange={field('requested_amount')} disabled={disabled} />
      </Field>
      <Field label="What the financing is for" hint="What the equipment will do for the business.">
        <Textarea rows={2} value={draft.intended_use} onChange={(e) => field('intended_use')(e.target.value)} disabled={disabled} />
      </Field>
      <Field label="How the payments will be made" hint="E.g. operating cash flow, a specific contract.">
        <Textarea rows={2} value={draft.repayment_source} onChange={(e) => field('repayment_source')(e.target.value)} disabled={disabled} />
      </Field>
      <FactFields
        label="Annual revenue"
        hint="Gross revenue of the business for the period you choose."
        draft={draft.annual_revenue}
        set={(u) => set((d) => ({ ...d, annual_revenue: u(d.annual_revenue) }))}
        disabled={disabled}
      />
      <FactFields
        label="Monthly debt payments"
        hint="What the business already pays each month on loans and leases."
        draft={draft.monthly_debt_service}
        set={(u) => set((d) => ({ ...d, monthly_debt_service: u(d.monthly_debt_service) }))}
        disabled={disabled}
      />
    </div>
  )
}

// ── equipment review ──

export function EquipmentReviewStepForm({
  draft,
  set,
  equipment,
  invoiceTotal,
  requested,
  disabled,
}: {
  draft: EquipmentReviewDraft
  set: Setter<EquipmentReviewDraft>
  equipment: EquipmentItemView[]
  invoiceTotal: MoneyView
  requested: MoneyView
  disabled?: boolean
}) {
  const flagFor = (id: string) => draft.item_flags.find((f) => f.equipment_item_id === id)?.note ?? ''
  const setFlag = (id: string, note: string) =>
    set((d) => ({
      ...d,
      item_flags: [...d.item_flags.filter((f) => f.equipment_item_id !== id), ...(note ? [{ equipment_item_id: id, note }] : [])],
    }))
  return (
    <div className="space-y-4">
      <ul className="divide-y divide-neutral-100 rounded-md border border-[var(--border-subtle)] bg-white">
        {equipment.map((e) => (
          <li key={e.id} className="space-y-2 p-3 text-sm">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span>
                <span className="font-medium">{[e.model_year, e.make, e.model].filter(Boolean).join(' ') || e.category}</span>
                <span className="text-neutral-500">
                  {' '}
                  · {humanize(e.condition)} · qty {e.quantity}
                </span>
              </span>
              <Mono>{formatAmount(e.line_total)}</Mono>
            </div>
            {draft.quote_matches === false && (
              <Field label="What is wrong with this item? (optional)">
                <Input value={flagFor(e.id)} onChange={(ev) => setFlag(e.id, ev.target.value)} disabled={disabled} />
              </Field>
            )}
          </li>
        ))}
      </ul>
      <dl className="rounded-md border border-[var(--border-subtle)] bg-neutral-50 px-3">
        <div className="flex justify-between py-1 text-sm">
          <dt className="text-neutral-500">Invoice total</dt>
          <dd>
            <Mono>{formatAmount(invoiceTotal)}</Mono>
          </dd>
        </div>
        <div className="flex justify-between py-1 text-sm">
          <dt className="text-neutral-500">Amount the dealer requested</dt>
          <dd>
            <Mono className="font-semibold">{formatAmount(requested)}</Mono>
          </dd>
        </div>
      </dl>

      <div role="radiogroup" aria-label="Does this match what you agreed?" className="space-y-2">
        <p className="text-sm font-medium text-neutral-700">Does this match what you agreed with the dealer?</p>
        {[
          { value: true, label: 'Yes, this is right' },
          { value: false, label: 'No, something is different' },
        ].map((opt) => (
          <label key={String(opt.value)} className="flex items-center gap-2 rounded-md border border-[var(--border-subtle)] bg-white px-3 py-2 text-sm">
            <input
              type="radio"
              name="quote_matches"
              checked={draft.quote_matches === opt.value}
              onChange={() => set((d) => (opt.value ? { quote_matches: true, note: '', item_flags: [] } : { ...d, quote_matches: false }))}
              disabled={disabled}
            />
            {opt.label}
          </label>
        ))}
      </div>
      {draft.quote_matches === false && (
        <Field label="What is different?" hint="Tex sorts this out with the dealer before anything goes further. Nothing you write here is shown to the dealership as-is.">
          <Textarea rows={3} value={draft.note} onChange={(e) => set((d) => ({ ...d, note: e.target.value }))} disabled={disabled} />
        </Field>
      )}
    </div>
  )
}
